import { reactive } from "vue";

// Global loading state for the top progress bar (TopProgressBar.vue).
//
// Two sources:
//  - route navigation (lazy page chunks + guards)      → setRouteLoading()
//  - in-flight API requests (axios interceptors)        → requestStarted()/requestFinished()
//
// Best-practice timing to avoid flicker:
//  - SHOW_DELAY: fast responses (< 180ms) never show the bar
//  - MIN_VISIBLE: once shown, the bar stays at least 350ms so it doesn't blink

const SHOW_DELAY = 180;
const MIN_VISIBLE = 350;

interface LoadingState {
  visible: boolean;
}

export const loadingState: LoadingState = reactive({ visible: false });

let routeLoading = false;
let pendingRequests = 0;
let showTimer: ReturnType<typeof setTimeout> | undefined;
let hideTimer: ReturnType<typeof setTimeout> | undefined;
let shownAt = 0;

function isBusy(): boolean {
  return routeLoading || pendingRequests > 0;
}

function update(): void {
  if (isBusy()) {
    clearTimeout(hideTimer);
    hideTimer = undefined;
    if (!loadingState.visible && !showTimer) {
      showTimer = setTimeout(() => {
        showTimer = undefined;
        if (isBusy()) {
          loadingState.visible = true;
          shownAt = Date.now();
        }
      }, SHOW_DELAY);
    }
    return;
  }

  clearTimeout(showTimer);
  showTimer = undefined;
  if (loadingState.visible && !hideTimer) {
    const remaining = Math.max(0, MIN_VISIBLE - (Date.now() - shownAt));
    hideTimer = setTimeout(() => {
      hideTimer = undefined;
      if (!isBusy()) loadingState.visible = false;
    }, remaining);
  }
}

export function setRouteLoading(value: boolean): void {
  routeLoading = value;
  update();
}

export function requestStarted(): void {
  pendingRequests += 1;
  update();
}

export function requestFinished(): void {
  pendingRequests = Math.max(0, pendingRequests - 1);
  update();
}
